// BHP-1062: line chart over an echarts instance.
//
// Rewritten from a class component that took `Promise`s as props and rendered
// into `document.getElementById(title + 'Chart')` from inside render(). That
// meant a side effect during render, a fresh echarts instance on every pass
// with nothing disposing the old one, and two charts sharing a title silently
// drawing into the same element. Data now arrives resolved (React Query does
// the awaiting) and the instance is bound to a ref with a proper teardown.
//
// BHP-1103: two defects left over from that rewrite, both found when an extra
// row on the detail page made the charts stop matching their container.
//
//  1. echarts fixes the canvas to the width it measures at init and never
//     looks again. A scrollbar appearing, a window resize or a collapsing
//     sidebar left the chart at its old width. Hence the ResizeObserver.
//  2. Creating the instance in the same effect as the data meant a full
//     dispose + init on every render, because `dates` and `data` are fresh
//     arrays each time. Setup now runs once; only the option follows the data.
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

interface Props {
  title: string;
  /** X-axis labels, one per data point. */
  dates: string[];
  data: number[];
}

export default function BaseChart({ title, dates, data }: Props): JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const element = container.current;

    if (element === null) {
      return;
    }

    const instance = echarts.init(element);
    chart.current = instance;

    const observer = new ResizeObserver(() => {
      instance.resize();
    });
    observer.observe(element);

    // Without this the instance survives every unmount, keeping its canvas
    // and internal listeners alive.
    return () => {
      observer.disconnect();
      instance.dispose();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    chart.current?.setOption({
      tooltip: {
        trigger: 'axis',
        position: (point: number[]) => [point[0], '10%']
      },
      grid: { top: 10, left: 45, right: 35 },
      xAxis: { type: 'category', boundaryGap: false, data: dates },
      yAxis: { type: 'value', boundaryGap: [0, '100%'] },
      series: [
        {
          name: `${title} data`,
          type: 'line',
          smooth: true,
          symbol: 'none',
          sampling: 'average',
          itemStyle: { color: 'rgb(255, 70, 131)' },
          areaStyle: {
            color: [
              { offset: 0, color: 'rgba(255, 158, 68, 0.2)' },
              { offset: 1, color: 'rgba(255, 70, 131)' }
            ]
          },
          data
        }
      ]
    });
  }, [title, dates, data]);

  return (
    <div className="rounded-xl border border-solid border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="mb-2 text-sm font-semibold text-slate-800">{title}</h4>
      <div ref={container} className="h-56 w-full" />
    </div>
  );
}
